import { queryOptions, useQuery } from '@tanstack/react-query';
import { ApiResponse, axiosInstance } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { FetchParams, IPagination } from '@/types/general.type';
import { TLabelCase } from '../types/label-case.type';

export type TGetListLabelCaseResponse = ApiResponse<IPagination<TLabelCase>>;
export type TGetListLabelCaseParams = FetchParams;

export const getListLabelCase = async (params: TGetListLabelCaseParams): Promise<TGetListLabelCaseResponse> => {
  const url = '/label-case';

  return axiosInstance.get(url, { params });
};

export const GET_LIST_LABEL_CASE_QUERY_KEY = 'label-case';

export const getListLabelCaseQueryOptions = <TData = TGetListLabelCaseResponse>(
  params: TGetListLabelCaseParams,
  select?: (data: TGetListLabelCaseResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_LABEL_CASE_QUERY_KEY,
      {
        page: params.page,
        limit: params.limit,
        search: params.search,
        sortDir: params.sortDir,
        sortBy: params.sortBy,
      },
    ],
    queryFn: () => getListLabelCase(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetListLabelCaseQuery<TData>(options: {
  params: TGetListLabelCaseParams;
  queryConfig?: QueryConfig<typeof getListLabelCaseQueryOptions<TData>>;
  select: (data: TGetListLabelCaseResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetListLabelCaseQuery(options: {
  params: TGetListLabelCaseParams;
  queryConfig?: QueryConfig<typeof getListLabelCaseQueryOptions<TGetListLabelCaseResponse>>;
}): ReturnType<typeof useQuery<TGetListLabelCaseResponse>>;

// Implementation
export function useGetListLabelCaseQuery<TData = TGetListLabelCaseResponse>({
  params,
  queryConfig,
  select,
}: {
  params: TGetListLabelCaseParams;
  queryConfig?: QueryConfig<typeof getListLabelCaseQueryOptions<TData | TGetListLabelCaseResponse>>;
  select?: (data: TGetListLabelCaseResponse) => TData;
}) {
  return useQuery({
    ...getListLabelCaseQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetListLabelCaseResponse ? TGetListLabelCaseResponse : TData>>;
}
